import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChatBubbleOutline as CommentsIcon,
  Send as SendIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import AddressbookPanelShell from "./AddressbookPanelShell";
import Gate from "../../../components/common/Gate";
import {
  apiGetComments,
  apiCreateComment,
  apiUpdateComment,
  apiDeleteComment,
} from "../../../graphQL/comment";

const DEFAULT_REF_COLLECTION = "contacts";
const PAGE_SIZE = 25;

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d)) return "";
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ── Comment Card ── */

const CommentCard = ({ comment, onEdit, onDelete }) => {
  const [hover, setHover] = useState(false);
  const text = comment.data?.text || "";
  const editedAt = comment.updatedAt !== comment.createdAt;

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        "&:hover": { backgroundColor: "#F9FAFB" },
        transition: "background-color 0.15s",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {formatTime(comment.createdAt)}
          {editedAt && " (edited)"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.25, ml: 1, flexShrink: 0 }}>
            <Gate flag="comments.card.edit">
            <IconButton
              size="small"
              onClick={() => onEdit(comment)}
              sx={{
                p: 0.25,
                color: "#9CA3AF",
                opacity: hover ? 1 : 0.85,
                "&:hover": { color: "#6B7280", opacity: 1 },
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            </Gate>
            <Gate flag="comments.card.delete">
            <IconButton
              size="small"
              onClick={() => onDelete(comment)}
              sx={{
                p: 0.25,
                color: "#9CA3AF",
                opacity: hover ? 1 : 0.85,
                "&:hover": { color: "#EF4444", opacity: 1 },
              }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
            </Gate>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        {text}
      </Typography>
    </Box>
  );
};

/* ── Main Panel ── */

const CommentsPanel = ({
  contact,
  refCollection,
  refId,
  refLabel,
  emptyMessage,
  authorId,
  onClose,
}) => {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const listRef = useRef(null);

  const effectiveRefCollection = refCollection || DEFAULT_REF_COLLECTION;
  const effectiveRefId = refId || contact?.id || null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Reset page when ref changes
  useEffect(() => {
    setPage(1);
    setComments([]);
    setTotal(0);
  }, [effectiveRefId, effectiveRefCollection]);

  const loadPage = useCallback(async (p) => {
    if (!effectiveRefId) return;
    setLoading(true);
    try {
      const res = await apiGetComments({
        refCollection: effectiveRefCollection,
        refId: effectiveRefId,
        ordering: { field: "createdAt", order: "Desc" },
        pagination: { page: p, limit: PAGE_SIZE },
      });
      setComments(res?.yrhComments || []);
      setTotal(res?.total ?? 0);
    } catch (err) {
      console.error("Failed to load comments:", err);
    } finally {
      setLoading(false);
    }
  }, [effectiveRefId, effectiveRefCollection]);

  useEffect(() => {
    loadPage(page);
  }, [loadPage, page]);

  // Scroll to top of list when page changes
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page]);

  const handleCreate = useCallback(async () => {
    const text = newText.trim();
    if (!text || !effectiveRefId || !authorId) return;
    setSubmitting(true);
    try {
      await apiCreateComment({
        authorId,
        data: { text },
        ref: { collection: effectiveRefCollection, id: effectiveRefId },
      });
      setNewText("");
      // Go to page 1 to see the newest comment (sorted desc)
      if (page === 1) {
        await loadPage(1);
      } else {
        setPage(1);
      }
    } catch (err) {
      console.error("Failed to create comment:", err);
    } finally {
      setSubmitting(false);
    }
  }, [newText, effectiveRefId, effectiveRefCollection, authorId, loadPage, page]);

  const handleUpdate = useCallback(async () => {
    const text = editText.trim();
    if (!text || !editingComment) return;
    setSubmitting(true);
    try {
      await apiUpdateComment({
        id: editingComment.id,
        data: { text },
      });
      setEditingComment(null);
      setEditText("");
      await loadPage(page);
    } catch (err) {
      console.error("Failed to update comment:", err);
    } finally {
      setSubmitting(false);
    }
  }, [editText, editingComment, loadPage, page]);

  const handleDelete = useCallback(async (comment) => {
    try {
      await apiDeleteComment(comment.id);
      // If we deleted the last item on this page, go back one page
      if (comments.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await loadPage(page);
      }
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  }, [loadPage, page, comments.length]);

  const handleStartEdit = useCallback((comment) => {
    setEditingComment(comment);
    setEditText(comment.data?.text || "");
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingComment(null);
    setEditText("");
  }, []);

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  const computedLabel = (() => {
    if (refLabel) return refLabel;
    const person = contact?.identity?.person || {};
    const first = (person.firstName || "").trim();
    const last = (person.lastName || "").trim();
    return `${first} ${last}`.trim();
  })();

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          <CommentsIcon sx={{ fontSize: 16, color: "#3B82F6" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>Comments</Typography>
        </Box>
      }
      footerLeft={
        computedLabel ? (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>{computedLabel}</Typography>
        ) : null
      }
      footerRight={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {total} comment{total !== 1 ? "s" : ""}
          </Typography>
          {totalPages > 1 ? (
            <>
              <IconButton size="small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: 11, color: "text.secondary", minWidth: 32, textAlign: "center" }}>
                {page} / {totalPages}
              </Typography>
              <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </>
          ) : null}
        </Box>
      }
    >
      {!effectiveRefId ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
            {emptyMessage || "Select an item to view comments."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          {/* Comment list */}
          <Box ref={listRef} sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 0.75, p: 1 }}>
            {loading && comments.length === 0 && (
              <Typography sx={{ color: "text.secondary", fontSize: 12, textAlign: "center", py: 2 }}>
                Loading...
              </Typography>
            )}
            {!loading && comments.length === 0 && (
              <Typography sx={{ color: "text.secondary", fontSize: 12, textAlign: "center", py: 4 }}>
                No comments yet. Add one below.
              </Typography>
            )}
            {comments.map((c) =>
              editingComment?.id === c.id ? (
                <Box key={c.id} sx={{ p: 1, border: "1px solid #3B82F6", borderRadius: 1.5, backgroundColor: "#EFF6FF" }}>
                  <TextField
                    fullWidth
                    multiline
                    autoFocus
                    maxRows={6}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, handleUpdate)}
                    size="small"
                    sx={{
                      mb: 0.75,
                      "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                    <IconButton size="small" onClick={handleCancelEdit} sx={{ p: 0.25, color: "#9CA3AF" }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleUpdate}
                      disabled={!editText.trim() || submitting}
                      sx={{ p: 0.25, color: "#3B82F6" }}
                    >
                      <SendIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <CommentCard
                  key={c.id}
                  comment={c}
                  onEdit={handleStartEdit}
                  onDelete={handleDelete}
                />
              )
            )}
          </Box>

          {/* New comment input */}
          <Box
            sx={{
              p: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "#FAFBFC",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-end" }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Write a comment..."
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleCreate)}
                size="small"
                sx={{
                  "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
                }}
              />
              <Gate flag="comments.send">
              <IconButton
                size="small"
                onClick={handleCreate}
                disabled={!newText.trim() || submitting}
                sx={{
                  color: newText.trim() ? "#3B82F6" : "#D1D5DB",
                  "&:hover": { color: "#2563EB" },
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
              </Gate>
            </Box>
          </Box>
        </Box>
      )}
    </AddressbookPanelShell>
  );
};

export default CommentsPanel;
